import React from 'react';
import { Info, History, Globe, Scale, MapPin, FileText } from 'lucide-react';

/**
 * Компонент с информационным содержимым о единицах измерения расстояний
 */
const InfoContent = ({ unitDefinitions }) => {
  // Фильтруем русские исторические единицы
  const russianUnits = Object.values(unitDefinitions).filter(unit => unit.system === 'russian');
  
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о единицах измерения расстояний
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          Системы измерения расстояний в мире
        </h3>
        <p>
          В современном мире существует несколько систем измерения расстояний, но наиболее распространены <strong>метрическая</strong> и <strong>имперская</strong> системы. Метрическая система используется в большинстве стран мира, включая Россию, в то время как имперская система по-прежнему широко применяется в США и частично в Великобритании.
        </p>
        
        <div className="not-prose my-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
              <h4 className="text-lg font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-1.5">
                <Scale className="w-4 h-4" />
                Метрическая система
              </h4>
              <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-400">
                <li>
                  <span className="font-medium">Основная единица:</span> метр (м)
                </li>
                <li>
                  <span className="font-medium">Страны:</span> Россия, Европа, большинство стран мира
                </li>
                <li>
                  <span className="font-medium">Кратные единицы:</span> километр (км), сантиметр (см), миллиметр (мм)
                </li>
                <li>
                  <span className="font-medium">Преимущества:</span> Десятичная система, простота перевода между единицами
                </li>
              </ul>
            </div>
            
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
              <h4 className="text-lg font-medium text-purple-800 dark:text-purple-300 mb-2 flex items-center gap-1.5">
                <Scale className="w-4 h-4" />
                Имперская система
              </h4>
              <ul className="space-y-2 text-sm text-purple-700 dark:text-purple-400">
                <li>
                  <span className="font-medium">Основные единицы:</span> дюйм (″), фут (ft), ярд (yd), миля (mi)
                </li>
                <li>
                  <span className="font-medium">Страны:</span> США, частично Великобритания
                </li>
                <li>
                  <span className="font-medium">Соотношения:</span> 12 дюймов = 1 фут, 3 фута = 1 ярд, 1760 ярдов = 1 миля
                </li>
                <li>
                  <span className="font-medium">Особенности:</span> Исторически сложившиеся пропорции, более сложный перевод между единицами
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <h3 className="flex items-center gap-2">
          <History className="w-5 h-5 text-amber-500" />
          Исторические русские меры длины
        </h3>
        <p>
          До введения метрической системы в России (которая официально была принята в 1899 году, а повсеместно внедрена после революции к 1924 году) использовались традиционные русские меры длины. Многие из них до сих пор встречаются в литературе, исторических документах и народных поговорках.
        </p>
        
        <div className="not-prose my-6 overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="bg-amber-50 dark:bg-amber-900/30">
                <th className="border border-amber-200 dark:border-amber-800 px-4 py-2 text-left text-amber-800 dark:text-amber-300">Название</th>
                <th className="border border-amber-200 dark:border-amber-800 px-4 py-2 text-left text-amber-800 dark:text-amber-300">Соответствие в метрической системе</th>
                <th className="border border-amber-200 dark:border-amber-800 px-4 py-2 text-left text-amber-800 dark:text-amber-300">Примечание</th>
              </tr>
            </thead>
            <tbody>
              {russianUnits.map((unit, index) => (
                <tr key={index} className="border-b border-amber-200 dark:border-amber-800">
                  <td className="border border-amber-200 dark:border-amber-800 px-4 py-2 font-medium">{unit.name} ({unit.shortName})</td>
                  <td className="border border-amber-200 dark:border-amber-800 px-4 py-2">{unit.baseConversion} м</td>
                  <td className="border border-amber-200 dark:border-amber-800 px-4 py-2">{unit.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <div className="flex gap-3">
            <FileText className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-base font-medium text-yellow-800 dark:text-yellow-300 mb-2">Интересные факты о русских мерах</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-yellow-700 dark:text-yellow-400">
                <li>Верста обозначалась специальными <strong>верстовыми столбами</strong> на дорогах России.</li>
                <li>Выражение «мерить версты» означало преодолевать большие расстояния пешком.</li>
                <li>Сажень происходит от способа измерения — расстояния между кончиками пальцев широко расставленных рук человека.</li>
                <li>Аршин первоначально означал длину шага, потом превратился в стандартную меру.</li>
                <li>Пушкин в произведениях часто использовал эти меры: «...до ближнего селенья не более версты».</li>
              </ul>
            </div>
          </div>
        </div>
        
        <h3 className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-red-500" />
          Современные российские обозначения расстояний
        </h3>
        <p>
          В современной России официально используется метрическая система, однако в разговорной речи и неформальных обозначениях иногда используются и другие меры:
        </p>
        
        <ul>
          <li><strong>«За МКАДом»</strong> — выражение, означающее территории за пределами Московской кольцевой автодороги, часто используется в шутку для обозначения удалённых от столицы регионов.</li>
          <li><strong>«Полтора часа на машине»</strong> — в России часто измеряют расстояния не в километрах, а во времени, необходимом на преодоление пути.</li>
          <li><strong>«В двух шагах»</strong> — обозначение очень близкого расстояния.</li>
          <li><strong>«Рукой подать»</strong> — идиоматическое выражение для обозначения близкого расстояния.</li>
        </ul>
        
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-base font-medium text-green-800 dark:text-green-300 mb-2">Полезные преобразования для путешествий по России</h4>
          <ul className="list-disc pl-5 space-y-1 text-sm text-green-700 dark:text-green-400">
            <li>1 километр ≈ 0.62 мили (полезно для общения с туристами из США)</li>
            <li>100 километров ≈ 62 мили</li>
            <li>1 метр ≈ 3.28 фута</li>
            <li>1 сантиметр ≈ 0.39 дюйма</li>
            <li>Расстояние Москва-Владивосток ≈ 9000 км (по прямой) или ≈ 5600 миль</li>
            <li>Протяженность России с запада на восток ≈ 10 000 км</li>
          </ul>
        </div>
        
        <h3>Заключение</h3>
        <p>
          Понимание различных единиц измерения расстояний важно не только для практических целей вроде путешествий или строительства, но и для понимания исторического и культурного контекста. Российская система мер имеет богатую историю, и хотя сегодня в стране используется метрическая система, знание исторических мер помогает лучше понимать литературу, историю и традиции России.
        </p>
        
        <p>
          Наш конвертер поможет вам легко переводить значения между различными единицами измерения, будь то современные метрические меры или исторические русские версты и аршины.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;